import React, { useState } from 'react';
import {
  string,
  shape,
  func,
} from 'prop-types';
import qs from 'query-string';
import {
  FlexContainer,
} from '@hyphengroup/cag-components';
import Spinner from 'components/common/Spinner';
import ErrorPage from 'pages/ErrorPage';
import { Query } from 'react-apollo';
import Layout from 'components/layout';
import { withTracking } from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import { withRedeemSubmission } from 'containers/withQueries';
import { getStaticText, getImages, getAuthQueryVariables } from 'helpers/Utils';
import GetRewardPage from 'money101/overrides/queries/getRewardPage.gql';
import RewardPageContent from 'money101/overrides/pages/Reward/RewardPageContent';

const goTo = (url) => {
  window.location.href = url;
};

const RewardPage = ({
  language,
  localisation,
  contentstackKeys,
  track,
  redeemSubmissionMutation,
  history,
}) => {
  const searchParams = qs.parse(history?.location?.search);
  const { imageAndLink, staticTextKeys } = contentstackKeys;
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectRewardData, setSelectRewardData] = useState(null);
  const [redeemRewardKey, setRedeemRewardKey] = useState(null);
  const [rewardRedeemToken, setRewardRedeemToken] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const selectReward = (item, rewardKey, redeemToken) => {
    setSelectRewardData(item);
    setRedeemRewardKey(rewardKey);
    setRewardRedeemToken(redeemToken);
  };

  const handleReject = (details) => {
    setLoading(false);
    setErrorMessage(details);
  };

  return (
    <Query
      query={GetRewardPage}
      variables={{
        language: localisation[language],
        ...getAuthQueryVariables({
          contentstackKeys,
          initailStaticTextKeys: [
            ...imageAndLink.rewardSelection,
            ...staticTextKeys.rewardSelection,
          ],
          initialImages: [...imageAndLink.rewardSelection],
        }),
        product: '獎勵',
        rewardToken: searchParams?.rewardToken || '',
      }}
    >
      {({
        loading,
        data,
        error,
        refetch,
      }) => {
        if (loading || isLoading) {
          return (
            <FlexContainer alignItems="center" justifyContent="center" height="80vh">
              <Spinner primary medium />
            </FlexContainer>
          );
        }
        const staticTextList = getStaticText(data);
        const imageAndLinkList = getImages(data);

        const redeemSubmissionWithoutPopup = (item, rewardKey, redeemToken) => {
          // Show loading spinner since next mutation is synchronous
          setLoading(true);
          redeemSubmissionMutation({
            variables: {
              input: [
                { key: rewardKey, value: item.key },
              ],
              token: redeemToken,
              language: localisation[language],
            },
          }).then(executeResultData => {
            // eslint-disable-next-line no-underscore-dangle
            if (executeResultData?.data?.redeemSubmission?.feedback.__typename === 'RewardAccepted') {
              goTo(`/${staticTextList.DASHBOARD_ROUTE_NAME}`);
            } else {
              handleReject(executeResultData);
            }
          }).catch(executeResultError => {
            handleReject(executeResultError);
          });

          track({
            data: {
              event: 'login_dashboard_rewards_claim_now',
              location: 'login - dashboard page',
              intent: 'claim rewards now',
              dashboardModule: 'rewards',
            },
          });
        };

        const successEvent = () => {
          // Show loading spinner since next mutation is synchronous
          setLoading(true);
          redeemSubmissionMutation({
            variables: {
              input: [
                { key: redeemRewardKey, value: selectRewardData.key },
              ],
              token: rewardRedeemToken,
              language: localisation[language],
            },
          }).then(executeResultData => {
            // eslint-disable-next-line no-underscore-dangle
            if (executeResultData?.data?.redeemSubmission?.feedback.__typename === 'RewardAccepted') {
              goTo(`/${staticTextList.DASHBOARD_ROUTE_NAME}`);
            } else {
              handleReject(executeResultData);
            }
          }).catch(executeResultError => {
            handleReject(executeResultError);
          });

          track({
            data: {
              event: 'login_dashboard_rewards_claim_now',
              location: 'login - dashboard page',
              intent: 'claim rewards now',
              dashboardModule: 'rewards',
            },
          });
        };

        return (
          <Layout
            {...data?.pageConfig}
            seoMetadata={data?.seoMetadata}
            onSuccessEvent={successEvent}
            customerProfile={data?.customerProfile}
            staticText={staticTextList}
            images={imageAndLinkList}
            refetch={refetch}
          >
            {
              (!searchParams?.rewardToken || errorMessage) && (
                <ErrorPage errorCode={errorMessage || 404} />
              )
            }
            {
              searchParams?.rewardToken && !errorMessage && (
                <RewardPageContent
                  data={data}
                  error={error}
                  searchParams={searchParams}
                  selectReward={selectReward}
                  redeemSubmissionWithoutPopup={redeemSubmissionWithoutPopup}
                  imageAndLinkList={imageAndLinkList}
                  staticTextList={staticTextList}
                />
              )
            }
          </Layout>
        );
      }
      }
    </Query>
  );
};

RewardPage.propTypes = {
  match: shape({}).isRequired,
  localisation: shape({}).isRequired,
  language: string.isRequired,
  contentstackKeys: shape({}),
  redeemSubmissionMutation: func.isRequired,
  track: func.isRequired,
  history: shape({}).isRequired,
};

RewardPage.defaultProps = {
  contentstackKeys: {},
};

export default withTracking(withApplicationConfig(withRedeemSubmission(RewardPage), ['languages', 'contentstackKeys']));
